/**
 * useSegmentAudio — Per-segment audio generation & version management
 *
 * Manages audio versions for each segment. Supports two providers:
 * - "preview": Web Speech API (TTS local, instant, free)
 * - "premium": Gemini via backend POST /api/v1/ecad/sintetizar
 *
 * IMPORTANT: Generation tracking is SEPARATED by source.
 * Preview and Premium buttons operate independently.
 */
import { useState, useCallback, useRef } from 'react';

// ── Types ──

export interface AudioVersion {
  id: string;
  text: string;
  textHash: string;
  voiceId: string;
  source: 'preview' | 'premium';
  audioUrl: string;       // object URL (blob) or server URL
  duration: number;       // seconds
  timestamp: number;
}

export interface SegmentAudioState {
  versions: AudioVersion[];
  activePreviewIdx: number;   // active preview version (-1 = none)
  activePremiumIdx: number;   // active premium version (-1 = none)
}

export type GenerationStatus = 'idle' | 'generating' | 'done' | 'error';

// ── Hash ──

function hashText(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i++) {
    h = ((h << 5) - h + text.charCodeAt(i)) | 0;
  }
  return String(h);
}

function makeId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// ── SessionStorage helpers for premium audio persistence ──

const AUDIO_STORE_KEY = 'ecad_segAudio';

function restoreStore(): Map<number, SegmentAudioState> {
  try {
    const saved = sessionStorage.getItem(AUDIO_STORE_KEY);
    if (!saved) return new Map();
    const entries: [number, SegmentAudioState][] = JSON.parse(saved);
    // Only restore premium versions (TTS preview has no audioUrl — must be re-spoken)
    const filtered = entries.map(([idx, state]) => {
      const premiumVersions = state.versions.filter(v => v.source === 'premium' && v.audioUrl);
      if (premiumVersions.length === 0) return null;
      return [idx, {
        versions: premiumVersions,
        activePreviewIdx: -1,
        activePremiumIdx: premiumVersions.length - 1,
      }] as [number, SegmentAudioState];
    }).filter(Boolean) as [number, SegmentAudioState][];
    if (filtered.length > 0) {
      console.log(`[useSegmentAudio] 🔄 Restaurados ${filtered.length} segmentos premium do sessionStorage`);
    }
    return new Map(filtered);
  } catch {
    return new Map();
  }
}

function persistStore(store: Map<number, SegmentAudioState>) {
  try {
    // Only persist premium versions with server URLs
    const entries = Array.from(store.entries()).map(([idx, state]) => {
      const premiumVersions = state.versions.filter(v => v.source === 'premium' && v.audioUrl);
      if (premiumVersions.length === 0) return null;
      return [idx, { ...state, versions: premiumVersions }];
    }).filter(Boolean);
    if (entries.length > 0) {
      sessionStorage.setItem(AUDIO_STORE_KEY, JSON.stringify(entries));
    }
  } catch {
    // sessionStorage full or unavailable — silent
  }
}

// ── Hook ──

export function useSegmentAudio() {
  // Map<segmentIndex, SegmentAudioState>
  const [store, setStore] = useState<Map<number, SegmentAudioState>>(() => restoreStore());
  // Separate tracking per source
  const [generatingPreview, setGeneratingPreview] = useState<Set<number>>(new Set());
  const [generatingPremium, setGeneratingPremium] = useState<Set<number>>(new Set());
  const [playingSegIdx, setPlayingSegIdx] = useState<number | null>(null);
  const [playingSource, setPlayingSource] = useState<'preview' | 'premium' | null>(null);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const cancelRef = useRef(false);
  const audioElRef = useRef<HTMLAudioElement | null>(null);

  // ── State getters ──

  const getState = useCallback((segIdx: number): SegmentAudioState => {
    return store.get(segIdx) || { versions: [], activePreviewIdx: -1, activePremiumIdx: -1 };
  }, [store]);

  const getActiveVersion = useCallback((segIdx: number, source?: 'preview' | 'premium'): AudioVersion | null => {
    const s = store.get(segIdx);
    if (!s) return null;
    if (source === 'preview') {
      if (s.activePreviewIdx < 0 || s.activePreviewIdx >= s.versions.length) return null;
      return s.versions[s.activePreviewIdx];
    }
    if (source === 'premium') {
      if (s.activePremiumIdx < 0 || s.activePremiumIdx >= s.versions.length) return null;
      return s.versions[s.activePremiumIdx];
    }
    // No source specified: return whichever exists (prefer preview for backwards compat)
    const pv = s.activePreviewIdx >= 0 && s.activePreviewIdx < s.versions.length ? s.versions[s.activePreviewIdx] : null;
    const pm = s.activePremiumIdx >= 0 && s.activePremiumIdx < s.versions.length ? s.versions[s.activePremiumIdx] : null;
    return pv || pm;
  }, [store]);

  /**
   * getActiveVersionForVoice — find the most recent version matching source + voiceId.
   * This is the KEY function for multi-voice versioning:
   * Each (source, voiceId) combination is tracked independently.
   */
  const getActiveVersionForVoice = useCallback((segIdx: number, source: 'preview' | 'premium', voiceId: string): AudioVersion | null => {
    const s = store.get(segIdx);
    if (!s) return null;
    // Find the most recent version for this exact source + voice
    const matching = s.versions
      .filter(v => v.source === source && v.voiceId === voiceId)
      .sort((a, b) => b.timestamp - a.timestamp);
    return matching[0] || null;
  }, [store]);

  /**
   * needsGeneration — checks if a segment needs (re)generation for a given source + voice.
   * voiceId is REQUIRED to properly detect when the user changed voice.
   */
  const needsGeneration = useCallback((segIdx: number, currentText: string, source: 'preview' | 'premium', voiceId?: string): boolean => {
    // If voiceId provided, use voice-aware lookup
    if (voiceId) {
      const active = getActiveVersionForVoice(segIdx, source, voiceId);
      if (!active) return true;
      return active.textHash !== hashText(currentText);
    }
    // Fallback: legacy behavior (no voiceId)
    const active = getActiveVersion(segIdx, source);
    if (!active) return true;
    return active.textHash !== hashText(currentText);
  }, [getActiveVersion, getActiveVersionForVoice]);

  /**
   * getGenerationStatus — returns status for a given source + voice combination.
   */
  const getGenerationStatus = useCallback((segIdx: number, source: 'preview' | 'premium', voiceId?: string): GenerationStatus => {
    if (source === 'preview' && generatingPreview.has(segIdx)) return 'generating';
    if (source === 'premium' && generatingPremium.has(segIdx)) return 'generating';
    const active = voiceId
      ? getActiveVersionForVoice(segIdx, source, voiceId)
      : getActiveVersion(segIdx, source);
    if (active) return 'done';
    return 'idle';
  }, [generatingPreview, generatingPremium, getActiveVersion, getActiveVersionForVoice]);

  /**
   * hasGeneratedVersion — quick boolean check for UI crown icon.
   */
  const hasGeneratedVersion = useCallback((segIdx: number, source: 'preview' | 'premium', voiceId: string): boolean => {
    return !!getActiveVersionForVoice(segIdx, source, voiceId);
  }, [getActiveVersionForVoice]);

  const allGenerated = useCallback((segments: Array<{ index: number; text: string }>, source: 'preview' | 'premium', voiceId?: string): boolean => {
    return segments.every(seg => !needsGeneration(seg.index, seg.text, source, voiceId));
  }, [needsGeneration]);

  // ── Add version helper ──

  const addVersion = useCallback((segIdx: number, version: AudioVersion) => {
    setStore(prev => {
      const next = new Map(prev);
      const existing = next.get(segIdx) || { versions: [], activePreviewIdx: -1, activePremiumIdx: -1 };
      const newVersions = [...existing.versions, version];
      const newIdx = newVersions.length - 1;
      next.set(segIdx, {
        versions: newVersions,
        activePreviewIdx: version.source === 'preview' ? newIdx : existing.activePreviewIdx,
        activePremiumIdx: version.source === 'premium' ? newIdx : existing.activePremiumIdx,
      });
      // Persist premium audio URLs to sessionStorage
      if (version.source === 'premium' && version.audioUrl) {
        persistStore(next);
      }
      return next;
    });
  }, []);

  // ── Generate Preview (Web Speech API) ──

  const generatePreview = useCallback(async (
    segIdx: number, text: string, voiceId: string,
    getVoice: () => SpeechSynthesisVoice | null
  ): Promise<void> => {
    if (!text.trim()) return;

    // Guard: prevent concurrent generation for same segment
    if (generatingPreview.has(segIdx)) return;

    // Check if already generated for this exact text
    const active = getActiveVersion(segIdx, 'preview');
    if (active && active.textHash === hashText(text)) return;

    setGeneratingPreview(prev => new Set(prev).add(segIdx));

    return new Promise<void>((resolve) => {
      if (!window.speechSynthesis) {
        console.warn('[useSegmentAudio] speechSynthesis not available — skipping preview generation');
        setGeneratingPreview(prev => { const n = new Set(prev); n.delete(segIdx); return n; });
        resolve();
        return;
      }
      const synth = window.speechSynthesis;
      synth.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      const voice = getVoice();
      if (voice) utterance.voice = voice;
      utterance.lang = 'pt-BR';
      utterance.rate = 1.0;

      const startTime = performance.now();

      utterance.onend = () => {
        const durationSec = (performance.now() - startTime) / 1000;

        const version: AudioVersion = {
          id: makeId(),
          text,
          textHash: hashText(text),
          voiceId,
          source: 'preview',
          audioUrl: '', // TTS preview doesn't produce a file — we re-speak on play
          duration: durationSec,
          timestamp: Date.now()
        };

        addVersion(segIdx, version);
        setGeneratingPreview(prev => { const n = new Set(prev); n.delete(segIdx); return n; });
        resolve();
      };

      utterance.onerror = () => {
        setGeneratingPreview(prev => { const n = new Set(prev); n.delete(segIdx); return n; });
        resolve();
      };

      synth.speak(utterance);
    });
  }, [getActiveVersion, addVersion]);

  // ── Generate Premium (Gemini via backend) ──

  const generatePremium = useCallback(async (
    segIdx: number, text: string, voiceId: string,
    masterToken?: string,
    context?: { prevText?: string; nextText?: string },
    targetDuration?: number
  ): Promise<void> => {
    if (!text.trim()) return;

    // Guard: prevent concurrent generation for same segment
    if (generatingPremium.has(segIdx)) return;

    // Check if already generated for this exact text + voice
    const active = getActiveVersionForVoice(segIdx, 'premium', voiceId);
    if (active && active.textHash === hashText(text)) return;

    setGeneratingPremium(prev => new Set(prev).add(segIdx));

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (masterToken) headers['Authorization'] = `Bearer ${masterToken}`;

      const body: Record<string, any> = { text, voice: voiceId, force: false };
      if (context) body.context = context;
      if (targetDuration && targetDuration > 0) body.targetDuration = targetDuration;

      const resp = await fetch('/api/v1/ecad/sintetizar', {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const data = await resp.json();

      const version: AudioVersion = {
        id: makeId(),
        text,
        textHash: hashText(text),
        voiceId,
        source: 'premium',
        audioUrl: data.audioUrl || '',
        duration: data.duration || 0,
        timestamp: Date.now()
      };

      addVersion(segIdx, version);
    } catch (err) {
      console.error(`[useSegmentAudio] Premium generation failed for seg ${segIdx}:`, err);
    } finally {
      setGeneratingPremium(prev => { const n = new Set(prev); n.delete(segIdx); return n; });
    }
  }, [getActiveVersion, addVersion]);

  // ── Generate All Pending ──

  const generateAllPending = useCallback(async (
    segments: Array<{ index: number; text: string; startSec?: number; endSec?: number }>,
    source: 'preview' | 'premium',
    voiceId: string,
    getVoice?: () => SpeechSynthesisVoice | null,
    masterToken?: string
  ) => {
    cancelRef.current = false;
    setIsGeneratingAll(true);

    for (let i = 0; i < segments.length; i++) {
      if (cancelRef.current) break;
      const seg = segments[i];
      if (!needsGeneration(seg.index, seg.text, source, voiceId)) continue;

      if (source === 'preview' && getVoice) {
        await generatePreview(seg.index, seg.text, voiceId, getVoice);
      } else if (source === 'premium') {
        // Build context from surrounding segments for continuity
        const prevTexts = segments.slice(0, i).map(s => s.text);
        const nextSeg = segments[i + 1];
        const context = {
          prevText: prevTexts.length > 0 ? prevTexts.join(' ') : undefined,
          nextText: nextSeg?.text || undefined,
        };
        // Calculate target duration from segment timing
        const targetDuration = (seg.startSec != null && seg.endSec != null)
          ? seg.endSec - seg.startSec
          : undefined;
        await generatePremium(seg.index, seg.text, voiceId, masterToken, context, targetDuration);
      }

      // Small gap between segments
      if (!cancelRef.current) {
        await new Promise(r => setTimeout(r, 150));
      }
    }

    setIsGeneratingAll(false);
  }, [needsGeneration, generatePreview, generatePremium]);

  const cancelGenerateAll = useCallback(() => {
    cancelRef.current = true;
    window.speechSynthesis?.cancel();
    setIsGeneratingAll(false);
  }, []);

  // ── Play Audio ──

  const playSegmentAudio = useCallback((
    segIdx: number,
    source: 'preview' | 'premium',
    getVoice?: () => SpeechSynthesisVoice | null
  ) => {
    const version = getActiveVersion(segIdx, source);
    if (!version) return;

    // Stop any current playback
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current = null;
    }
    window.speechSynthesis?.cancel();

    if (playingSegIdx === segIdx && playingSource === source) {
      // Toggle off
      setPlayingSegIdx(null);
      setPlayingSource(null);
      return;
    }

    setPlayingSegIdx(segIdx);
    setPlayingSource(source);

    if (version.source === 'preview') {
      // Re-speak via Web Speech API
      if (!window.speechSynthesis) {
        console.warn('[useSegmentAudio] speechSynthesis not available — cannot play preview');
        setPlayingSegIdx(null);
        setPlayingSource(null);
        return;
      }
      const utterance = new SpeechSynthesisUtterance(version.text);
      const voice = getVoice?.();
      if (voice) utterance.voice = voice;
      utterance.lang = 'pt-BR';
      utterance.onend = () => { setPlayingSegIdx(null); setPlayingSource(null); };
      utterance.onerror = () => { setPlayingSegIdx(null); setPlayingSource(null); };
      window.speechSynthesis.speak(utterance);
    } else if (version.audioUrl) {
      // Play from URL
      const audio = new Audio(version.audioUrl);
      audioElRef.current = audio;
      audio.onended = () => { setPlayingSegIdx(null); setPlayingSource(null); audioElRef.current = null; };
      audio.onerror = () => {
        console.error(`[useSegmentAudio] Audio error for seg ${segIdx}:`, audio.error?.message || 'unknown');
        setPlayingSegIdx(null); setPlayingSource(null); audioElRef.current = null;
      };
      audio.play().catch((err) => {
        console.error(`[useSegmentAudio] Play failed for seg ${segIdx}:`, err?.message || err);
        setPlayingSegIdx(null); setPlayingSource(null);
      });
    } else {
      // No audioUrl — nothing to play
      console.warn(`[useSegmentAudio] Seg ${segIdx} has version but no audioUrl — source: ${version.source}`);
      setPlayingSegIdx(null);
      setPlayingSource(null);
    }
  }, [getActiveVersion, playingSegIdx, playingSource]);

  const stopPlayback = useCallback(() => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current = null;
    }
    window.speechSynthesis?.cancel();
    setPlayingSegIdx(null);
    setPlayingSource(null);
  }, []);

  // ── Version Navigation ──

  const navigateVersion = useCallback((segIdx: number, targetIndex: number, source: 'preview' | 'premium'): AudioVersion | null => {
    const state = store.get(segIdx);
    if (!state || targetIndex < 0 || targetIndex >= state.versions.length) return null;

    setStore(prev => {
      const next = new Map(prev);
      const s = next.get(segIdx)!;
      next.set(segIdx, {
        ...s,
        ...(source === 'preview' ? { activePreviewIdx: targetIndex } : { activePremiumIdx: targetIndex })
      });
      return next;
    });

    return state.versions[targetIndex];
  }, [store]);

  const getVersionCount = useCallback((segIdx: number, source?: 'preview' | 'premium'): number => {
    const s = store.get(segIdx);
    if (!s) return 0;
    if (!source) return s.versions.length;
    return s.versions.filter(v => v.source === source).length;
  }, [store]);

  // ── Duration getter for timeline health ──
  const getAudioDuration = useCallback((segIdx: number, source: 'preview' | 'premium', voiceId?: string): number | null => {
    const v = voiceId
      ? getActiveVersionForVoice(segIdx, source, voiceId)
      : getActiveVersion(segIdx, source);
    return v ? v.duration : null;
  }, [getActiveVersion, getActiveVersionForVoice]);

  return {
    // State
    getState,
    getActiveVersion,
    getActiveVersionForVoice,
    hasGeneratedVersion,
    needsGeneration,
    getGenerationStatus,
    allGenerated,
    getVersionCount,
    getAudioDuration,
    // Generation
    generatePreview,
    generatePremium,
    generateAllPending,
    cancelGenerateAll,
    isGeneratingAll,
    // Playback
    playSegmentAudio,
    stopPlayback,
    playingSegIdx,
    playingSource,
    // Version navigation
    navigateVersion,
  };
}
